import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const TermsOfService = () => {
  const lastUpdated = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="container mx-auto py-12 px-4 sm:px-6 lg:px-8 max-w-5xl">
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-400 bg-clip-text text-transparent mb-4">
          Terms of Service
        </h1>
        <p className="text-gray-500 text-lg max-w-3xl mx-auto">
          Last updated: {lastUpdated}
        </p>
        <Separator className="my-8" />
      </div>


	{/* 1. Purpose and Scope */}
	<Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="purpose-and-scope">
	  <CardHeader>
		<CardTitle className="text-2xl font-bold text-gray-800">
		  1. Purpose and Scope
		</CardTitle>
	  </CardHeader>
	  <CardContent className="text-gray-600 space-y-4">
		<p>
		  These General Terms and Conditions of Sale and Use (the <strong>“T&Cs”</strong>) define the rights and obligations applicable to the use of the Digika.ai platform (the <strong>“Platform”</strong>), operated by <strong>DIGIKA SASU</strong>.
		</p>

		<div>
		  <p className="font-semibold text-gray-700">The Platform enables:</p>
		  <ol className="list-decimal pl-6 space-y-1">
			<li>
			  Connecting companies (<strong>“Clients”</strong>) with independent service providers (<strong>“Freelancers”</strong>).
			</li>
			<li>
			  Securing payments through regulated payment service providers (<strong>PayPal Business</strong>, <strong>Binance Pay</strong>).
			</li>
			<li>
			  Providing, from the launch of the <strong>DGK Token</strong>, an option to settle assignments partially or fully in DGK Token once the assignment has been validated.
			</li>
		  </ol>
		</div>

		<p>
		  These T&Cs apply, without restriction or reservation, to any User (Client or Freelancer) of the Platform.
		</p>
		<p>
		  By creating an account or accepting an assignment, the User acknowledges having read and fully accepted these T&Cs.
		</p>
	  </CardContent>
	</Card>

	{/* 2. Access and Transaction Evidence */}
	<Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="access-and-transaction">
	  <CardHeader>
		<CardTitle className="text-2xl font-bold text-gray-800">
		  2. Access and Transaction Evidence
		</CardTitle>
	  </CardHeader>
	  <CardContent className="text-gray-600 space-y-4">
		<div>
		  <p className="font-semibold text-gray-700">2.1 Access to Services</p>
		  <p>
			Access to the Digika.ai Platform is reserved for duly registered Users,
			whether Freelancers or Clients. Each User must provide accurate
			information upon registration and keep their login details confidential.
			Any login or data transmission performed from the User’s account shall
			be deemed to have been performed by them.
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">2.2 Validation of Transactions</p>
		  <p>
			When a Client posts an assignment and a Freelancer is selected, the order
			is considered binding as soon as both parties validate it through the
			Platform. The corresponding payment is immediately placed in escrow with
			the regulated payment service provider (<strong>PayPal Business</strong> or{" "}
			<strong>Binance Pay</strong>).
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">2.3 Evidential Value of Records</p>
		  <p>
			The data recorded by the Platform and/or the regulated payment service
			providers (<strong>PSPs</strong>) shall constitute proof of the
			transactions and commitments between the parties. In case of dispute,
			electronic records (timestamps, payment confirmations, communications)
			shall be deemed conclusive between the User and Digika.ai.
		  </p>
		</div>
	  </CardContent>
	</Card>

	{/* 3. Prices, Commissions and Payments */}
	<Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="prices-commissions-payments">
	  <CardHeader>
		<CardTitle className="text-2xl font-bold text-gray-800">
		  3. Prices, Commissions and Payments
		</CardTitle>
	  </CardHeader>
	  <CardContent className="text-gray-600 space-y-4">
		<div>
		  <p className="font-semibold text-gray-700">3.1 Free Registration</p>
		  <p>
			Registration and account creation on the Platform are free of charge for
			both Freelancers and Clients.
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">3.2 Digika.ai Commission</p>
		  <p>
			For each validated assignment on the Platform, Digika.ai charges a
			commission calculated on the total pre-tax amount paid by the Client:
		  </p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>10% for payments made in fiat currency (credit card, PayPal, etc.)</li>
			<li>5% for payments made in stablecoins (USDT/USDC via Binance Pay)</li>
			<li>
			  1% for payments made in <strong>DGK Token</strong>, once this option
			  becomes available
			</li>
		  </ul>
		  <p className="mt-2">This commission covers:</p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>use of the Platform</li>
			<li>PSP fees</li>
			<li>mediation in case of disputes</li>
			<li>Digika’s professional liability insurance</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">3.3 VAT and Taxes</p>
		  <p>
			The commission charged by Digika.ai is subject to French VAT (20%),
			applied only to the commission amount. The Freelancer remains solely
			responsible for the declaration and payment of any taxes applicable to
			their services (VAT, income tax, social contributions, etc.).
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">3.4 Payment Terms</p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>
			  Client payments are held in escrow by the regulated PSP (PayPal
			  Business or Binance Pay).
			</li>
			<li>
			  Once the assignment is validated by both parties, the PSP releases the
			  funds to the Freelancer, after automatic deduction of Digika.ai’s
			  commission.
			</li>
			<li>
			  In case of payment in <strong>DGK Token</strong> (post-TGE), the
			  transfer shall be triggered from Digika’s secure multi-signature
			  wallet (<strong>Gnosis Safe</strong>) to the Freelancer’s wallet.
			</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">3.5 Payment Security</p>
		  <p>
			Digika.ai never holds Client funds. All financial flows are operated by
			regulated PSPs. Digika does not store any banking data of the Users.
		  </p>
		</div>
	  </CardContent>
	</Card>

	{/* 4. DGK Token: Utility and Limitations */}
	<Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="dgk-token">
	  <CardHeader>
		<CardTitle className="text-2xl font-bold text-gray-800">
		  4. DGK Token: Utility and Limitations
		</CardTitle>
	  </CardHeader>
	  <CardContent className="text-gray-600 space-y-4">
		<div>
		  <p className="font-semibold text-gray-700">4.1 Nature of the DGK Token</p>
		  <p>
			The <strong>DGK Token</strong> is a utility token within the meaning of Regulation (EU) 2023/1114 (“MiCA”), enabling access to certain features of the Digika.ai Platform. It is in no way:
		  </p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>a financial instrument within the meaning of the French Monetary and Financial Code,</li>
			<li>an electronic money token (“EMT”),</li>
			<li>an asset-referenced token (“ART”),</li>
			<li>or an equity security conferring rights to DIGIKA SASU’s share capital, profits, or assets.</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">4.2 Utility of the DGK Token within the Digika.ai Ecosystem</p>
		  <p><strong>DGK Tokens</strong> may be used by Users exclusively within the Platform, notably to:</p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>pay commissions and fees due to Digika.ai,</li>
			<li>obtain discounts on service fees,</li>
			<li>access premium services (profile or project promotion, priority access to offers, advanced AI tools),</li>
			<li>participate in consultative governance mechanisms implemented by Digika.ai (non-binding community votes on Platform developments).</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">4.3 Payments and Conversions</p>
		  <p>When a Freelancer opts for payment in DGK Token:</p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>the initial funds remain processed in fiat or stablecoins via the regulated PSPs,</li>
			<li>Digika subsequently triggers an equivalent DGK Token transfer from its secure multi-signature wallet (<strong>Gnosis Safe</strong>),</li>
			<li>the conversion rate is determined at the time of payment or assignment validation, depending on the option chosen by the Freelancer (see Article 3.4).</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">4.4 No Guarantee of Value</p>
		  <p>
			As DGK Tokens are subject to market conditions, their value may fluctuate. Digika.ai provides no guarantee as to the future value, stability, or liquidity of DGK Tokens. No refund, compensation, or forced conversion into fiat currency is provided.
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">4.5 No Financial Rights</p>
		  <p>
			The acquisition or use of DGK Tokens does not confer any financial rights vis-à-vis Digika.ai, in particular:
		  </p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>no right to dividends,</li>
			<li>no right to guaranteed returns,</li>
			<li>no claim or redemption rights against DIGIKA SASU.</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">4.6 Risks Related to DGK Tokens</p>
		  <p>Users acknowledge and accept the inherent risks of using blockchain-based tokens, including:</p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>price volatility,</li>
			<li>transaction fees (“gas fees”),</li>
			<li>technical risks (cyberattacks, loss of private keys, network congestion),</li>
			<li>and regulatory risks depending on jurisdiction.</li>
		  </ul>
		</div>
	  </CardContent>
	</Card>

	{/* 5. Assignment Process */}
	<Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="assignment-process">
	  <CardHeader>
		<CardTitle className="text-2xl font-bold text-gray-800">
		  5. Assignment Process
		</CardTitle>
	  </CardHeader>
	  <CardContent className="text-gray-600 space-y-4">
		<div>
		  <p className="font-semibold text-gray-700">5.1 Posting an Assignment</p>
		  <p>
			The Client publishes their assignment through the Platform, specifying
			the essential information: description, budget, deadlines, and any
			specific conditions. The Freelancer may then apply and propose their
			terms.
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">5.2 Acceptance of the Assignment</p>
		  <p>
			The assignment is deemed accepted when:
		  </p>
		  <ol className="list-decimal pl-6 space-y-1">
			<li>The Client validates the Freelancer’s application, and</li>
			<li>
			  The corresponding payment is made by the Client and held in escrow with
			  the regulated payment service provider (<strong>PSP</strong>).
			</li>
		  </ol>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">5.3 Performance and Delivery</p>
		  <p>
			The Freelancer undertakes to perform the assignment in accordance with
			the validated description. Delivery of deliverables must be made
			exclusively via the Platform to ensure traceability and security of
			exchanges.
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">5.4 Validation and Release of Payment</p>
		  <p>
			Upon receipt of deliverables, the Client has a period of 7 business days
			to validate the assignment or report a dispute.
		  </p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>
			  In the event of express validation (confirmation click) or tacit
			  validation (no objection within the deadline), the PSP shall
			  automatically release the payment to the Freelancer, after deduction of
			  Digika.ai’s commission.
			</li>
			<li>
			  In the event of a dispute, the internal mediation procedure (Article 6)
			  shall be initiated.
			</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">5.5 Amendments and Addenda</p>
		  <p>
			Any substantial modification of an assignment (budget, deadlines, scope)
			must be validated by both parties through the Platform. Such modification
			may result in an adjustment of the amount held in escrow.
		  </p>
		</div>
	  </CardContent>
	</Card>

	{/* 6. Dispute Management and Mediation */}
	<Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="dispute-management">
	  <CardHeader>
		<CardTitle className="text-2xl font-bold text-gray-800">
		  6. Dispute Management and Mediation
		</CardTitle>
	  </CardHeader>
	  <CardContent className="text-gray-600 space-y-4">
		<div>
		  <p className="font-semibold text-gray-700">6.1 Reporting a Dispute</p>
		  <p>
			In case of disagreement between the Client and the Freelancer (quality
			of deliverables, deadline, compliance, etc.), the concerned User must
			report the dispute through the dedicated Platform interface before the
			automatic release of funds.
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">6.2 Role of Digika.ai</p>
		  <p>
			Digika.ai acts as a mediating third party to help the parties reach an
			amicable solution:
		  </p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>Digika collects evidence (deliverables, exchanges, deadlines, initial conditions).</li>
			<li>Digika formulates a resolution proposal, without any binding decision-making power.</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">6.3 Final Decision and Financial Flows</p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>
			  If an agreement is reached, Digika forwards the decision to the PSP,
			  which releases the funds according to the agreed terms.
			</li>
			<li>
			  If no agreement is reached, the dispute is handled under the PSP’s
			  internal procedures (e.g., PayPal Business, Binance Pay), whose
			  decision is binding on the parties.
			</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">6.4 Professional Liability Insurance</p>
		  <p>
			Digika.ai maintains professional liability insurance covering its
			mediation services. This insurance does not cover disputes relating to
			the execution of assignments between Clients and Freelancers.
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">6.5 Limitation of Digika.ai’s Liability</p>
		  <p>
			Digika.ai is not a party to the contract entered into between the Client
			and the Freelancer. Its role is limited to:
		  </p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>connecting the parties,</li>
			<li>facilitating the transaction via regulated PSPs,</li>
			<li>and proposing mediation in case of dispute.</li>
		  </ul>
		  <p>
			Under no circumstances does Digika guarantee the success, quality, or
			compliance of assignments performed.
		  </p>
		</div>
	  </CardContent>
	</Card>

	{/* 7. Intellectual Property */}
	<Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="intellectual-property">
	  <CardHeader>
		<CardTitle className="text-2xl font-bold text-gray-800">
		  7. Intellectual Property
		</CardTitle>
	  </CardHeader>
	  <CardContent className="text-gray-600 space-y-4">
		<div>
		  <p className="font-semibold text-gray-700">7.1 Rights Related to the Platform</p>
		  <p>
			All elements constituting the Digika.ai Platform (texts, graphics, logos,
			databases, algorithms, matching AI, software, etc.) are the exclusive
			property of <strong>DIGIKA SASU</strong> and are protected under French and
			international intellectual property law. Any reproduction, distribution,
			modification, or use without prior written authorization from DIGIKA SASU
			is strictly prohibited.
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">7.2 Rights to Freelancers’ Deliverables</p>
		  <p>
			Unless otherwise stated in the assignment specifications, intellectual
			property rights over deliverables produced by the Freelancer are
			transferred to the Client only after full payment of the assignment. The
			Freelancer retains their moral rights (authorship and right to be
			credited).
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">7.3 Warranties by Freelancers</p>
		  <p>The Freelancer warrants that the deliverables provided:</p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>are original creations, or that they hold the necessary rights to use and transfer them,</li>
			<li>do not infringe upon third-party rights (trademarks, patents, copyrights, software licenses).</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">7.4 Liability in Case of Infringement</p>
		  <p>
			In the event of a claim or legal action related to intellectual property
			infringement by a deliverable, the Freelancer shall be solely liable.
			Digika.ai shall not, under any circumstances, be held liable for
			infringements committed by a User.
		  </p>
		</div>
	  </CardContent>
	</Card>

	{/* 8. Limitation of Liability */}
	<Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="limitation-liability">
	  <CardHeader>
		<CardTitle className="text-2xl font-bold text-gray-800">
		  8. Limitation of Liability
		</CardTitle>
	  </CardHeader>
	  <CardContent className="text-gray-600 space-y-4">
		<div>
		  <p className="font-semibold text-gray-700">8.1 Limited Role of Digika.ai</p>
		  <p>
			Digika.ai acts solely as a technical and commercial intermediary,
			connecting Clients and Freelancers. Digika.ai is not a party to the
			contract entered directly between the Client and the Freelancer and does
			not guarantee the success, quality, or compliance of the services
			provided.
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">8.2 Platform Operations</p>
		  <p>
			Digika.ai undertakes to use all reasonable means to ensure continuous and
			secure access to the Platform. However, Digika.ai cannot be held liable
			for:
		  </p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>temporary interruptions due to maintenance, updates, or technical upgrades,</li>
			<li>failures or malfunctions attributable to third-party providers (hosts, PSPs, network operators, etc.),</li>
			<li>or unavailability resulting from force majeure (see Article 9).</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">8.3 User Responsibilities</p>
		  <p>Each User is solely responsible for:</p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>the accuracy of the information they provide,</li>
			<li>compliance with their tax, social, and legal obligations,</li>
			<li>and the use of deliverables (compliance, licenses, copyrights, etc.).</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">8.4 Limitation of Damages</p>
		  <p>
			In any event, Digika.ai’s liability, if incurred, shall be strictly
			limited to the amount of commissions received by Digika.ai in connection
			with the disputed assignment. Digika.ai shall in no case be held liable
			for indirect damages (loss of revenue, data, reputation, or opportunity).
		  </p>
		</div>
	  </CardContent>
	</Card>

	{/* 9. Personal Data Protection */}
	<Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="personal-data-protection">
	  <CardHeader>
		<CardTitle className="text-2xl font-bold text-gray-800">
		  9. Personal Data Protection
		</CardTitle>
	  </CardHeader>
	  <CardContent className="text-gray-600 space-y-4">
		<div>
		  <p className="font-semibold text-gray-700">9.1 Compliance with Regulations</p>
		  <p>
			Digika.ai undertakes to comply fully with all applicable regulations on
			personal data protection, and in particular Regulation (EU) 2016/679,
			known as the <strong>GDPR</strong>.
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">9.2 Data Collected by Digika.ai</p>
		  <p>
			In the context of using the Platform, Digika.ai collects only the data
			strictly necessary for the provision of its services, namely:
		  </p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>account information (first name, last name, professional email, password),</li>
			<li>professional information (profile, skills, experience, CV),</li>
			<li>transaction metadata (amounts, currencies, payment identifiers).</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">9.3 Data Not Collected by Digika.ai</p>
		  <p>Digika.ai does not collect or store:</p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>Users’ full banking details,</li>
			<li>KYC/AML documents (identity card, proof of address, etc.).</li>
		  </ul>
		  <p>
			Such data is processed directly and exclusively by regulated payment
			service providers (<strong>PayPal Business</strong>, <strong>Binance Pay</strong>).
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">9.4 Purposes of Processing</p>
		  <p>The data collected by Digika.ai is used solely to:</p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>enable connections between Clients and Freelancers,</li>
			<li>ensure the monitoring of transactions and commissions,</li>
			<li>guarantee account security and traceability in case of disputes,</li>
			<li>comply with applicable legal and tax obligations.</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">9.5 Hosting and Security</p>
		  <p>
			Data is hosted in data centers located within the European Union.
			Digika.ai implements all necessary technical and organizational measures
			to guarantee confidentiality, integrity, and security of data
			(encryption, restricted access, logging).
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">9.6 Users’ Rights</p>
		  <p>
			In accordance with the GDPR, each User has the right to access, rectify,
			object to, port, and delete their personal data. These rights may be
			exercised at any time through the user dashboard or by contacting:{" "}
			<a href="mailto:dpo@digika.ai" className="text-blue-600 underline">
			  dpo@digika.ai
			</a>.
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">9.7 Data Retention</p>
		  <p>
			Data is retained for the period strictly necessary for the provision of
			services and for compliance with legal obligations (notably accounting
			and tax).
		  </p>
		</div>
	  </CardContent>
	</Card>

	{/* 10. Force Majeure */}
	<Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="force-majeure">
	  <CardHeader>
		<CardTitle className="text-2xl font-bold text-gray-800">
		  10. Force Majeure
		</CardTitle>
	  </CardHeader>
	  <CardContent className="text-gray-600 space-y-4">
		<div>
		  <p className="font-semibold text-gray-700">10.1 Definition</p>
		  <p>
			A force majeure event is any unforeseeable, irresistible, and external
			occurrence preventing the normal performance of contractual obligations.
			Events constituting force majeure include, without limitation:
		  </p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>natural disasters, fires, floods, storms, earthquakes,</li>
			<li>epidemics, pandemics, public health measures or exceptional government decisions,</li>
			<li>widespread power or telecommunications outages,</li>
			<li>prolonged failure of essential service providers (hosts, PSPs, internet providers),</li>
			<li>general strikes, labor disputes, acts of war, terrorism, large-scale cyberattacks.</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">10.2 Effects of Force Majeure</p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>The performance of Digika.ai’s obligations is suspended for the duration of the event,</li>
			<li>No compensation or penalty may be claimed from Digika.ai due to such suspension,</li>
			<li>
			  If the event persists for more than 30 days, either party may terminate
			  the affected contract by simple written notice, without entitlement to
			  compensation from either side.
			</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">10.3 Obligation to Notify</p>
		  <p>
			The party affected by a force majeure event must notify the other party
			as soon as possible and provide all useful information regarding the
			occurrence and expected duration of the event.
		  </p>
		</div>
	  </CardContent>
	</Card>

	{/* 11. User Commitments */}
	<Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="user-commitments">
	  <CardHeader>
		<CardTitle className="text-2xl font-bold text-gray-800">
		  11. User Commitments
		</CardTitle>
	  </CardHeader>
	  <CardContent className="text-gray-600 space-y-4">
		<div>
		  <p className="font-semibold text-gray-700">11.1 Fair Use of the Platform</p>
		  <p>
			Each User undertakes to use the Platform honestly and fairly, in strict
			compliance with applicable laws and regulations. In particular, Users are
			prohibited from:
		  </p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>diverting the Platform for fraudulent, illegal, or unlawful purposes,</li>
			<li>
			  bypassing the Platform by attempting to contract directly outside of it,
			  in order to avoid paying commissions due to Digika.ai,
			</li>
			<li>
			  using scripts, bots, or any automated process to collect data or
			  interact abusively with the Platform.
			</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">11.2 Specific Commitments of Clients</p>
		  <p>The Client undertakes to:</p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>post only genuine, lawful assignments, in compliance with labor law and best practices,</li>
			<li>
			  not post discriminatory assignments (gender, origin, religion, sexual
			  orientation, age, disability, etc.), in accordance with the French Labor
			  Code,
			</li>
			<li>remunerate the Freelancer according to the conditions agreed and validated on the Platform.</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">11.3 Specific Commitments of Freelancers</p>
		  <p>The Freelancer undertakes to:</p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>provide accurate information about their skills, experience, and references,</li>
			<li>carry out assignments diligently, professionally, and in accordance with the validated specifications,</li>
			<li>
			  ensure that their services do not infringe upon third-party intellectual
			  property or contractual rights.
			</li>
		  </ul>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">11.4 Confidentiality of Credentials</p>
		  <p>
			Each User is responsible for keeping their login credentials confidential.
			Any action carried out through their account shall be deemed performed by
			them, unless proven otherwise.
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">11.5 Compliance with Applicable Law</p>
		  <p>Users acknowledge that:</p>
		  <ul className="list-disc pl-6 space-y-1">
			<li>
			  any assignment carried out in France must be drafted in French (French
			  Labor Code, art. L.311-4),
			</li>
			<li>
			  they remain solely responsible for complying with their tax, social, and
			  legal obligations (self-employed status, URSSAF, VAT, income tax, etc.).
			</li>
		  </ul>
		</div>
	  </CardContent>
	</Card>

	{/* 12. Governing Law and Jurisdiction */}
	<Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="governing-law">
	  <CardHeader>
		<CardTitle className="text-2xl font-bold text-gray-800">
		  12. Governing Law and Jurisdiction
		</CardTitle>
	  </CardHeader>
	  <CardContent className="text-gray-600 space-y-4">
		<div>
		  <p className="font-semibold text-gray-700">12.1 Governing Law</p>
		  <p>
			These Terms and Conditions shall be governed by and construed in
			accordance with French law, to the exclusion of any other legislation.
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">12.2 Amicable Settlement</p>
		  <p>
			In the event of a dispute between a User and Digika.ai, the parties
			shall endeavor to reach an amicable solution, in particular through the
			internal mediation service provided for in Article 6.
		  </p>
		</div>

		<div>
		  <p className="font-semibold text-gray-700">12.3 Jurisdiction</p>
		  <p>
			Failing amicable settlement, any dispute relating to the validity,
			interpretation, or performance of these Terms and Conditions shall fall
			within the exclusive jurisdiction of the courts of Paris (France), even
			in cases involving multiple defendants or third-party claims.
		  </p>
		</div>
	  </CardContent>
	</Card>

      <div className="text-center mt-12 text-sm text-gray-500">
        <p>© {new Date().getFullYear()} Digika.ai. All rights reserved.</p>
      </div>
    </div>
  );
};

export default TermsOfService;
